import { useEffect, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR } from "../lib/tokens";
import { useTheme as useMCMTheme } from "../lib/theme";
import { pageTitle } from "../lib/mcmTokens";
import { useToast } from "../lib/toast";
import SchedulePicker from "./SchedulePicker";
import IAteThisSheet from "./IAteThisSheet";
import MealDetailDrawer from "./MealDetailDrawer";
import CalendarDay from "./CalendarDay";
import CalendarWeek from "./CalendarWeek";
import CalendarMonth from "./CalendarMonth";
import WeeklyPlanner from "./planner/WeeklyPlanner";
import { useScheduledMeals } from "../lib/useScheduledMeals";
import { startConceptExpansion } from "../lib/expandConcept";
import { retryBackgroundRecipeGen } from "../lib/backgroundRecipeGen";
import { useUserRecipes } from "../lib/useUserRecipes";
import { useCommunityLeaderboard } from "../lib/useCommunityRecipes";
import { useMealPrepQueue } from "../lib/useMealPrepQueue";
import { dayImageFor } from "../lib/dayImages";
import { RECIPES, findRecipe, totalTimeMin, difficultyLabel } from "../data/recipes";
import { supabase } from "../lib/supabase";
import {
  startOfDay,
  addDays,
  addMonths,
  dateAtHour,
} from "../lib/calendarUtils";

// ─────────────────────────────────────────────────────────────────────────────
// Calendar tab — Day / Week / Month views with hour-grid drilldown.
//
// View hierarchy:
//   - Day   (default landing) — hour grid 6 AM – 10 PM. Tiles render
//                                at their scheduled hour. Past cooks
//                                inline muted with ✓ badge.
//   - Week                    — 7 columns × hour rows. Tap a column
//                                header → switch to Day view for that date.
//   - Month                   — 6×7 grid with dot indicators. Tap a
//                                cell → Day view for that date.
//
// View persists in localStorage so the user's pick survives reloads.
// Week-start (Sunday vs Monday) is derived from getMeasurementSystem()
// — US users get Sunday-first; metric users get Monday-first. No
// separate setting.
// ─────────────────────────────────────────────────────────────────────────────

const VIEW_KEY = "mise.calendar.view.v1";
const VIEWS = ["day", "week", "month"];

function readStoredView() {
  if (typeof localStorage === "undefined") return "day";
  const v = localStorage.getItem(VIEW_KEY);
  return VIEWS.includes(v) ? v : "day";
}
function writeStoredView(v) {
  if (typeof localStorage === "undefined") return;
  try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ }
}

export default function Plan({
  profile, userId, familyKey, nameFor, hasFamily,
  family = [], friends = [],
  pantry = [], setPantry,
  shoppingList = [], setShoppingList, onGoToShopping,
  onOpenCook, onStartCook,
  deepLink, onDeepLinkConsumed,
  onCreateRecipe,            // () => void — open the Create flow (empty-picker CTA)
}) {
  const { theme: mcmTheme } = useMCMTheme();
  const { push: pushToast } = useToast();

  // View + cursor state. Cursor is the "currently focused date" — Day
  // view shows that day, Week view shows that day's week, Month view
  // shows that day's month.
  const [view, setView] = useState(readStoredView);
  const [cursorDate, setCursorDate] = useState(() => startOfDay(new Date()));

  useEffect(() => { writeStoredView(view); }, [view]);

  // ── Data window ──────────────────────────────────────────────
  // Wide window (±45 days from cursor) so most navigation within
  // Week / Month views doesn't refetch. Re-fetches when the cursor
  // moves into uncovered range (e.g. user advances 2 months ahead).
  const dataWindow = useMemo(() => {
    const start = startOfDay(addDays(cursorDate, -45));
    const end = startOfDay(addDays(cursorDate, 45));
    return { start, end };
  }, [cursorDate]);

  const { recipes: userRecipesList, findBySlug: findUserRecipe, setSharing: setRecipeSharing, saveRecipe } = useUserRecipes(userId);
  // Community recipes for the schedule picker — cached leaderboard (top
  // ~100, 24h TTL), so no per-open egress. Metadata only; the full body is
  // fetched on pick.
  const { rows: communityRecipeRows } = useCommunityLeaderboard();

  const { meals, loading, schedule, cancel, claim, unclaim, updateMeal } = useScheduledMeals(userId, {
    fromISO: dataWindow.start.toISOString(),
    toISO:   dataWindow.end.toISOString(),
    familyKey,
    recipeResolver: (slug, ownerUserId) => findRecipe(slug, findUserRecipe, ownerUserId),
  });

  const mealIds = useMemo(() => meals.map(m => m.id), [meals]);
  const { rows: prepRowsAll, byMeal: prepByMeal, dismiss: dismissPrep, undismiss: undismissPrep } =
    useMealPrepQueue(userId, mealIds);

  // Past cooks within the same window. RLS scopes to self + family.
  const today = useMemo(() => startOfDay(new Date()), []);
  const [pastCooks, setPastCooks] = useState([]);
  useEffect(() => {
    let alive = true;
    if (!userId) { setPastCooks([]); return; }
    (async () => {
      const { data, error } = await supabase
        .from("cook_logs")
        .select("id, user_id, recipe_slug, recipe_title, recipe_emoji, recipe_cuisine, recipe_category, rating, notes, diners, cooked_at, xp_earned")
        .gte("cooked_at", dataWindow.start.toISOString())
        .lte("cooked_at",  dataWindow.end.toISOString())
        .order("cooked_at", { ascending: false });
      if (!alive) return;
      if (error) { console.warn("[past cooks] load failed:", error.message); setPastCooks([]); return; }
      setPastCooks(data || []);
    })();
    return () => { alive = false; };
  }, [userId, familyKey, dataWindow.start, dataWindow.end]);

  // ── UI state for layered modals ──────────────────────────────
  // RecipePickerModal opens when user taps an empty hour cell or a "+"
  // anywhere on the calendar. After they pick a recipe, SchedulePicker
  // opens with initialDate (and optional initialHour) seeded.
  const [pickingForDate, setPickingForDate] = useState(null);     // Date — day to schedule on
  const [pickingForHour, setPickingForHour] = useState(null);     // hour 0-23 — pre-seeds time
  const [recipeToSchedule, setRecipeToSchedule] = useState(null); // chosen recipe object
  const [scheduleFromPantryRowId, setScheduleFromPantryRowId] = useState(null);
  const [isRequesting, setIsRequesting] = useState(false);
  const [openMeal, setOpenMeal] = useState(null);
  const [eatingLeftover, setEatingLeftover] = useState(null);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [expandingMealId, setExpandingMealId] = useState(null);

  // Leftover candidates surface above the recipe list in the picker.
  const leftoverCandidates = useMemo(() => {
    const rows = (pantry || []).filter(p =>
      p?.kind === "meal"
      && Number(p.servingsRemaining) > 0
      && p.sourceRecipeSlug,
    );
    rows.sort((a, b) => {
      const ea = a.expiresAt ? new Date(a.expiresAt).getTime() : Infinity;
      const eb = b.expiresAt ? new Date(b.expiresAt).getTime() : Infinity;
      return ea - eb;
    });
    return rows.map(pantryRow => ({
      pantryRow,
      recipe: findRecipe(pantryRow.sourceRecipeSlug, findUserRecipe),
    }));
  }, [pantry, findUserRecipe]);

  // Deep-link consumer — push tap on prep reminder / cook timer hands
  // us { kind, id }.
  //
  // cook_session deep-links resolve IMMEDIATELY without waiting on
  // the meals list (participant cooks aren't always in the viewer's
  // meals — Marissa joining Trevor's Carbonara doesn't have a row
  // in her scheduled_meals). Other kinds (scheduled_meal) still
  // wait for meals.
  useEffect(() => {
    if (!deepLink) return;
    if (deepLink.kind !== "cook_session" && meals.length === 0) return;
    const consume = () => onDeepLinkConsumed?.();
    if (deepLink.kind === "scheduled_meal") {
      const meal = meals.find(m => m.id === deepLink.id);
      if (meal) {
        setOpenMeal(meal);
        // Jump cursor to the meal's day so when the drawer closes,
        // the user lands on the right day.
        setCursorDate(startOfDay(new Date(meal.scheduled_for)));
        setView("day");
        consume();
      }
      return;
    }
    if (deepLink.kind === "cook_session") {
      (async () => {
        const { data } = await supabase
          .from("cook_sessions")
          .select("id, recipe_slug, status, user_id")
          .eq("id", deepLink.id)
          .maybeSingle();
        if (!data) { consume(); return; }

        // Team-cook deep-link: if the session is ACTIVE, jump
        // straight into CookMode joined to that session — the
        // helper/diner sees the lead's live state, doesn't create
        // their own duplicate cook. Auto-accept their participation
        // row so they're never asked to manually confirm (the
        // invite IS the acceptance).
        if (data.status === "active") {
          // Auto-accept this user's participant row (fire-and-forget)
          supabase
            .from("cook_session_participants")
            .update({ accepted_at: new Date().toISOString() })
            .eq("cook_session_id", data.id)
            .is("accepted_at", null)
            .then(({ error }) => {
              if (error) console.warn("[plan] auto-accept failed:", error.message);
            });
        }

        if (data.recipe_slug && data.status === "active") {
          // ALWAYS prefer the SESSION OWNER's user_recipe over the
          // bundled fallback. The viewer's `findRecipe` would grab
          // any bundled recipe with the same slug — wrong when the
          // lead is cooking a personal variant ("(Not-So-Authentic)
          // Spaghetti Carbonara" vs the generic bundled "Carbonara").
          // The bundled fallback only kicks in if the lead's recipe
          // can't be SELECTed (no row, or RLS blocks it).
          let recipe = null;
          try {
            const { data: ur } = await supabase
              .from("user_recipes")
              .select("recipe, slug")
              .eq("slug", data.recipe_slug)
              .eq("user_id", data.user_id)
              .maybeSingle();
            if (ur?.recipe) {
              recipe = { ...ur.recipe, slug: ur.slug, _userId: data.user_id };
            }
          } catch (e) {
            console.warn("[plan] team-cook recipe fetch:", e?.message || e);
          }
          // Final fallback — viewer's library or bundled. Only used
          // when the lead's recipe couldn't be loaded.
          if (!recipe) recipe = findRecipe(data.recipe_slug, findUserRecipe);

          // Pre-fetch the lead's current step so CookMode opens on
          // the right step on first paint (no flash of step 1).
          let stepIndex = undefined;
          if (recipe?.steps?.length) {
            try {
              const { data: openStep } = await supabase
                .from("cook_session_steps")
                .select("step_id")
                .eq("cook_session_id", data.id)
                .is("finished_at", null)
                .order("started_at", { ascending: false })
                .limit(1)
                .maybeSingle();
              if (openStep?.step_id) {
                const target = String(openStep.step_id).replace(/-s\d+$/, "");
                const idx = recipe.steps.findIndex((s) => String(s.id) === target);
                if (idx >= 0) stepIndex = idx;
              }
            } catch {}
          }
          if (recipe) {
            onStartCook?.(recipe, {
              joinSessionId: data.id,
              view: "cook",
              ...(Number.isFinite(stepIndex) ? { stepIndex } : {}),
            });
            consume();
            return;
          }
        }

        // Fall-through: my own session or a finished session →
        // open the meal drawer if one exists, else start fresh.
        const meal = meals.find(m => m.recipe_slug === data.recipe_slug);
        if (meal) setOpenMeal(meal);
        else if (data.recipe_slug) {
          const recipe = findRecipe(data.recipe_slug, findUserRecipe);
          if (recipe) onStartCook?.(recipe);
        }
        consume();
      })();
    }
  }, [deepLink, meals, onDeepLinkConsumed, findUserRecipe, onStartCook]);

  // ── Schedule write — wraps the bug-fixing try/catch per plan ─
  const onSaveSchedule = useCallback(async ({ scheduledFor, notificationSettings, note, cookId, isRequest, servings, mealSlot, eatAnchor, day2Hour }) => {
    if (!recipeToSchedule) return;
    // No past-time gate — users can retro-log meals they already ate.
    // The calendar UI flags past-time slots distinctly so the user
    // knows they're logging-in-arrears, not planning forward.

    // Auto-share on schedule when the meal is family-visible (request
    // or family-cook) AND the recipe is the viewer's own user_recipes
    // row. Without this, family members see the meal but can't read
    // the recipe (locked-fallback drawer).
    const authorIsSelf = recipeToSchedule._userId && recipeToSchedule._userId === userId;
    const familyVisible = isRequest || (cookId && cookId !== userId);
    if (authorIsSelf && familyVisible && setRecipeSharing) {
      const ownRow = userRecipesList.find(r =>
        r.userId === userId && r.slug === recipeToSchedule.slug,
      );
      if (ownRow && !ownRow.shared) {
        setRecipeSharing(ownRow.id, { shared: true }).catch(e =>
          console.warn("[plan] auto-share on schedule failed:", e?.message || e),
        );
      }
    }

    try {
      await schedule({
        recipeSlug: recipeToSchedule.slug,
        recipeUserId: recipeToSchedule._userId || null,
        scheduledFor,
        notificationSettings,
        note,
        cookId,
        isRequest,
        servings,
        mealSlot,
        eatAnchor,
        day2Hour,
        fromPantryRowId: scheduleFromPantryRowId,
      });
      setScheduleFromPantryRowId(null);
      setIsRequesting(false);
      setRecipeToSchedule(null);
      setPickingForDate(null);
      setPickingForHour(null);
      pushToast("Scheduled — see the calendar", { emoji: "📅", kind: "info" });
    } catch (e) {
      console.error("[plan] schedule failed:", e);
      pushToast(`Couldn't schedule: ${e?.message || "try again"}`, { emoji: "⚠️", kind: "warn" });
    }
  }, [recipeToSchedule, scheduleFromPantryRowId, userRecipesList, userId, schedule, setRecipeSharing, pushToast]);

  // ── View navigation handlers ─────────────────────────────────
  const jumpToday = () => setCursorDate(startOfDay(new Date()));
  const onPrevDay = () => setCursorDate(d => addDays(d, -1));
  const onNextDay = () => setCursorDate(d => addDays(d, 1));
  const onPrevWeek = () => setCursorDate(d => addDays(d, -7));
  const onNextWeek = () => setCursorDate(d => addDays(d, 7));
  const onPrevMonth = () => setCursorDate(d => addMonths(d, -1));
  const onNextMonth = () => setCursorDate(d => addMonths(d, 1));
  const onSelectDay = (d) => { setCursorDate(startOfDay(d)); setView("day"); };

  // ── Tap an empty hour → open the recipe picker for that slot ─
  const onTapEmptyHour = (date, hour) => {
    setPickingForDate(date);
    setPickingForHour(hour);
    setIsRequesting(false);
  };

  // ── Render ───────────────────────────────────────────────────
  const firstName = profile?.name?.trim().split(/\s+/)[0];
  const userName = firstName;

  // Compute SchedulePicker initial seed. If user tapped an empty
  // hour in Day view, prefer that (date + hour). Otherwise just the
  // tapped date with no hour preference.
  const initialDateForPicker = pickingForDate
    ? (pickingForHour != null ? dateAtHour(pickingForDate, pickingForHour) : pickingForDate)
    : null;

  // Day-of-week illustration for the active calendar cursor. Big,
  // anchored top-right of the entire tab.
  //
  // Alt-spawn behavior: dayImageFor rolls fresh each call. We memoize
  // by the calendar-day key so the roll fires ONCE per visit to a
  // given day — landing on Monday rolls (~12% chance of alt), and the
  // result stays stable until the cursor moves away. Coming back to
  // Monday rolls again, independently. The user sees the alt as a
  // rare reward; it doesn't stick around.
  const dayImageKey = `${cursorDate.getFullYear()}-${cursorDate.getMonth()}-${cursorDate.getDate()}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dayImage = useMemo(() => dayImageFor(cursorDate), [dayImageKey]);

  return (
    <div style={{ minHeight: "100vh", paddingBottom: 100, position: "relative" }}>
      {dayImage && (
        <img
          src={dayImage}
          alt=""
          aria-hidden="true"
          loading="lazy"
          style={{
            position: "absolute",
            top: "calc(env(safe-area-inset-top, 0px) + 12px)",
            right: 16,
            width: 140,
            height: "auto",
            borderRadius: 12,
            zIndex: 3,
            pointerEvents: "none",
          }}
        />
      )}
      <div style={{ padding: "calc(env(safe-area-inset-top, 0px) + 28px) 20px 0" }}>
        <div style={{ fontFamily: FONT.forma, fontSize: 10, fontWeight: 600, color: mcmTheme.color.skyInkMuted || COLOR.muted, letterSpacing: "0.16em", marginBottom: 6 }}>
          YOUR CALENDAR{firstName ? `, ${firstName.toUpperCase()}` : ""}
        </div>
        <h1 style={{
          ...pageTitle,
          color: (mcmTheme.color.skyInk || mcmTheme.color.ink),
        }}>
          Calendar
        </h1>
      </div>

      {/* View toggle — segmented pill */}
      <div style={{ padding: "16px 20px 14px" }}>
        <ViewToggle value={view} onChange={setView} theme={mcmTheme} />
      </div>


      {/* Active view */}
      <div style={{ padding: "0 20px" }}>
        {view === "day" && (
          <CalendarDay
            cursorDate={cursorDate}
            meals={meals}
            pastCooks={pastCooks}
            prepRows={prepRowsAll}
            pantry={pantry}
            userId={userId}
            nameFor={nameFor}
            findRecipe={findRecipe}
            findUserRecipe={findUserRecipe}
            onPrevDay={onPrevDay}
            onNextDay={onNextDay}
            onJumpToday={jumpToday}
            onTapMeal={setOpenMeal}
            onTapCook={onOpenCook}
            onTapEmpty={onTapEmptyHour}
            onRescheduleMeal={async (meal, newDate) => {
              try {
                await updateMeal(meal.id, { scheduled_for: newDate.toISOString() });
                pushToast(`Moved to ${newDate.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`, { emoji: "📅", kind: "info" });
              } catch (e) {
                console.error("[plan] reschedule failed:", e);
                pushToast(`Couldn't reschedule: ${e?.message || "try again"}`, { emoji: "⚠️", kind: "warn" });
              }
            }}
            onStartCook={(meal) => {
              // Same logic the MealDetailDrawer's COOK NOW button uses —
              // route leftovers to IAteThisSheet, otherwise resolve the
              // recipe (with optional servings scaling) and hand off to
              // App's openCookMode.
              if (meal.from_pantry_row_id) {
                const leftoverRow = (pantry || []).find(p => p.id === meal.from_pantry_row_id);
                if (leftoverRow) {
                  setEatingLeftover(leftoverRow);
                  return;
                }
              }
              const r = findRecipe(meal.recipe_slug, findUserRecipe, meal.recipe_user_id);
              if (!r) return;
              // Seed the scheduled serving count instead of pre-scaling —
              // CookMode derives its step-prose scale ratio from base
              // serves vs override, and a pre-scaled copy collapses the
              // ratio to 1 (prose keeps base quantities).
              const scaled = meal.servings && meal.servings !== r.serves
                ? { ...r, _initialServings: meal.servings }
                : r;
              onStartCook?.(scaled);
            }}
          />
        )}
        {view === "week" && (
          <CalendarWeek
            cursorDate={cursorDate}
            meals={meals}
            pastCooks={pastCooks}
            pantry={pantry}
            userId={userId}
            nameFor={nameFor}
            findRecipe={findRecipe}
            findUserRecipe={findUserRecipe}
            onPrevWeek={onPrevWeek}
            onNextWeek={onNextWeek}
            onSelectDay={onSelectDay}
            onTapMeal={setOpenMeal}
            onTapCook={onOpenCook}
          />
        )}
        {view === "month" && (
          <CalendarMonth
            cursorDate={cursorDate}
            meals={meals}
            pastCooks={pastCooks}
            findRecipe={findRecipe}
            findUserRecipe={findUserRecipe}
            onPrevMonth={onPrevMonth}
            onNextMonth={onNextMonth}
            onSelectDay={onSelectDay}
          />
        )}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 20, color: COLOR.sub, fontFamily: FONT.forma, fontSize: 12 }}>
          Loading your calendar…
        </div>
      )}

      {/* Layered modals */}
      {plannerOpen && (
        <WeeklyPlanner
          userId={userId}
          profile={profile}
          pantry={pantry}
          shoppingList={shoppingList}
          setShoppingList={setShoppingList}
          schedule={schedule}
          userRecipes={userRecipesList}
          findUserRecipe={findUserRecipe}
          familyCount={family.length}
          cursorDate={cursorDate}
          onClose={() => setPlannerOpen(false)}
        />
      )}
      {pickingForDate && !recipeToSchedule && (
        <RecipePickerModal
          userRecipes={userRecipesList}
          communityRecipes={communityRecipeRows}
          currentUserId={userId}
          leftovers={leftoverCandidates}
          onPick={(recipe, opts = {}) => {
            setRecipeToSchedule(recipe);
            setScheduleFromPantryRowId(opts.fromPantryRowId || null);
          }}
          onCreateRecipe={onCreateRecipe ? () => {
            setPickingForDate(null);
            setPickingForHour(null);
            setScheduleFromPantryRowId(null);
            onCreateRecipe();
          } : null}
          onClose={() => {
            setPickingForDate(null);
            setPickingForHour(null);
            setScheduleFromPantryRowId(null);
          }}
        />
      )}
      {recipeToSchedule && (
        <SchedulePicker
          recipe={recipeToSchedule}
          initialDate={initialDateForPicker}
          userId={userId}
          userName={userName}
          family={family}
          friends={friends}
          defaultRequest={hasFamily && isRequesting}
          onClose={() => {
            setRecipeToSchedule(null);
            setPickingForDate(null);
            setPickingForHour(null);
            setScheduleFromPantryRowId(null);
          }}
          onSave={onSaveSchedule}
        />
      )}
      {openMeal && (
        <MealDetailDrawer
          meal={openMeal}
          // Snapshot fallback: weekly-planner CONCEPT meals have a
          // placeholder slug findRecipe can't resolve — their pseudo-
          // recipe lives only in recipe_snapshot. Live lookup still
          // wins for real recipes (existing behavior unchanged).
          recipe={
            findRecipe(openMeal.recipe_slug, findUserRecipe, openMeal.recipe_user_id)
            || (openMeal.recipe_snapshot && typeof openMeal.recipe_snapshot === "object"
              ? openMeal.recipe_snapshot
              : null)
          }
          generating={expandingMealId === openMeal.id}
          onGenerateRecipe={async () => {
            if (expandingMealId) return;
            setExpandingMealId(openMeal.id);
            try {
              // Detached generation (no-waiting-page-of-death): this
              // resolves in ~1s after minting the stub + re-pointing
              // the meal; the recipe itself builds in the background
              // and the drawer flips live via user_recipes realtime.
              const updated = await startConceptExpansion({
                meal: openMeal,
                pantry,
                profile,
                saveRecipe,
                updateMeal,
              });
              if (updated) setOpenMeal(updated);
              pushToast("Writing the full recipe — it'll fill in right here", { emoji: "📖", kind: "info" });
            } catch (e) {
              console.error("[plan] concept expansion failed:", e);
              pushToast(e?.message || "Couldn't start generation — try again.", { emoji: "⚠️", kind: "warn" });
            } finally {
              setExpandingMealId(null);
            }
          }}
          onRetryGenerate={async () => {
            const row = userRecipesList.find(r => r.slug === openMeal.recipe_slug);
            if (!row?.id) {
              pushToast("Can't retry from here — regenerate from the meal's Create flow.", { emoji: "⚠️", kind: "warn" });
              return;
            }
            const started = await retryBackgroundRecipeGen(row.id);
            pushToast(
              started ? "Retrying — it'll fill in right here" : "Retry inputs are gone — regenerate from Create.",
              { emoji: started ? "📖" : "⚠️", kind: started ? "info" : "warn" },
            );
          }}
          userId={userId}
          nameFor={nameFor}
          family={family}
          prepRows={prepByMeal.get(openMeal.id) || []}
          onDismissPrep={dismissPrep}
          onUndismissPrep={undismissPrep}
          onClose={() => setOpenMeal(null)}
          onCookNow={() => {
            if (openMeal.from_pantry_row_id) {
              const leftoverRow = (pantry || []).find(p => p.id === openMeal.from_pantry_row_id);
              if (leftoverRow) {
                setOpenMeal(null);
                setEatingLeftover(leftoverRow);
                return;
              }
            }
            const r = findRecipe(openMeal.recipe_slug, findUserRecipe, openMeal.recipe_user_id);
            setOpenMeal(null);
            if (!r) return;
            // Seed, don't pre-scale — see the CalendarDay onCookNow
            // comment above (keeps CookMode's prose scaling honest).
            const scaled = openMeal.servings && openMeal.servings !== r.serves
              ? { ...r, _initialServings: openMeal.servings }
              : r;
            onStartCook?.(scaled);
          }}
          onClaim={async () => {
            const updated = await claim(openMeal.id);
            setOpenMeal(updated);
          }}
          onUnclaim={async () => {
            const updated = await unclaim(openMeal.id);
            setOpenMeal(updated);
          }}
          onChangeCook={async (cookId) => {
            const updated = await updateMeal(openMeal.id, { cook_id: cookId });
            setOpenMeal(updated);
          }}
          onChangeServings={async (servings) => {
            const updated = await updateMeal(openMeal.id, { servings });
            setOpenMeal(updated);
          }}
          onChangeScheduledFor={async (iso) => {
            const updated = await updateMeal(openMeal.id, { scheduled_for: iso });
            setOpenMeal(updated);
          }}
          onChangeEatAnchor={async (eatAnchor) => {
            const updated = await updateMeal(openMeal.id, { eat_anchor: eatAnchor });
            setOpenMeal(updated);
          }}
          onDelete={async (id) => {
            await cancel(id);
            setOpenMeal(null);
          }}
        />
      )}
      {eatingLeftover && (
        <IAteThisSheet
          pantryRow={eatingLeftover}
          userId={userId}
          onClose={() => setEatingLeftover(null)}
          onDone={async () => {
            const hits = (meals || []).filter(m => m.from_pantry_row_id === eatingLeftover.id && m.status === "planned");
            for (const m of hits) {
              try { await updateMeal(m.id, { status: "cooked" }); } catch (_) { /* non-fatal */ }
            }
            setEatingLeftover(null);
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// View toggle — segmented pill at top of the calendar.
// ─────────────────────────────────────────────────────────────────────────────

function ViewToggle({ value, onChange, theme }) {
  return (
    <div style={{
      display: "inline-flex",
      background: theme.color.glassFillHeavy,
      border: `1px solid ${theme.color.glassBorder}`,
      borderRadius: 999,
      padding: 3,
      boxShadow: theme.shadow.soft,
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
    }}>
      {VIEWS.map(v => {
        const active = v === value;
        return (
          <button
            key={v}
            onClick={() => onChange(v)}
            style={{
              padding: "8px 18px",
              border: "none",
              background: active ? COLOR.gold : "transparent",
              color: active ? "#241810" : (theme.color.skyInkMuted || COLOR.muted),
              borderRadius: 999,
              fontFamily: FONT.forma, fontSize: 11, fontWeight: 700,
              letterSpacing: "0.16em",
              cursor: "pointer",
              transition: "background 160ms ease, color 160ms ease",
            }}
          >
            {v.toUpperCase()}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// RecipePickerModal — opens when user taps "+" or an empty hour.
// Inline because it's only used here.
// ─────────────────────────────────────────────────────────────────────────────

// Loose substring + subsequence match. Fast-path on substring; falls
// back to "characters appear in order, not necessarily contiguous"
// so partial / typo-tolerant queries like `geto` still find
// `gjetost` (g-j-e-t-o-s-t). Empty queries are handled by the
// caller — this assumes a non-empty lowercase q.
function looselyMatches(target, q) {
  if (!target) return false;
  const t = String(target).toLowerCase();
  if (t.includes(q)) return true;
  let qi = 0;
  for (let i = 0; i < t.length && qi < q.length; i++) {
    if (t[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

// Recipe match across all the fields that carry meaningful text:
// title, cuisine, category, description, and ingredient names. The
// title was the only field searched before, which left distinctive
// ingredients (gjetost, nduja, harissa) invisible from the picker.
function recipeMatchesQuery(recipe, q) {
  if (!recipe) return false;
  if (looselyMatches(recipe.title,       q)) return true;
  if (looselyMatches(recipe.cuisine,     q)) return true;
  if (looselyMatches(recipe.category,    q)) return true;
  if (looselyMatches(recipe.description, q)) return true;
  if (Array.isArray(recipe.ingredients)) {
    for (const ing of recipe.ingredients) {
      if (looselyMatches(ing?.name, q)) return true;
    }
  }
  return false;
}

function RecipePickerModal({ userRecipes = [], communityRecipes = [], currentUserId = null, leftovers = [], onPick, onCreateRecipe, onClose }) {
  const [query, setQuery] = useState("");
  const [fetchingId, setFetchingId] = useState(null);
  const all = useMemo(() => {
    const bundledSlugs = new Set(RECIPES.map(r => r.slug));
    const userOnly = userRecipes
      .filter(r => r?.recipe && r.slug && !bundledSlugs.has(r.slug))
      .map(r => ({ ...r.recipe, slug: r.slug, _source: r.source, _userId: r.userId }));
    return [...RECIPES, ...userOnly];
  }, [userRecipes]);
  // Community recipes (cached leaderboard metadata). Deduped against the
  // bundled + the user's own/visible recipes, and their own authored ones
  // (already in `all`). Body is fetched on pick — these rows are metadata.
  const communityList = useMemo(() => {
    const bundledSlugs = new Set(RECIPES.map(r => r.slug));
    const userSlugs = new Set(userRecipes.map(r => r.slug));
    return (communityRecipes || [])
      .filter(c => c && c.recipe_id && c.title)
      .filter(c => c.author_id !== currentUserId)
      .filter(c => !bundledSlugs.has(c.slug) && !userSlugs.has(c.slug))
      .map(c => ({
        title: c.title, emoji: c.emoji, cuisine: c.cuisine, slug: c.slug,
        goalImageURL: c.goal_image_url,
        _source: "community", _communityRecipeId: c.recipe_id, _authorName: c.author_name,
      }));
  }, [communityRecipes, userRecipes, currentUserId]);
  const filteredLeftovers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leftovers;
    return leftovers.filter(l => recipeMatchesQuery(l.recipe, q));
  }, [query, leftovers]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter(r => recipeMatchesQuery(r, q));
  }, [query, all]);
  const filteredCommunity = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = q ? communityList.filter(r => recipeMatchesQuery(r, q)) : communityList;
    return base.slice(0, 30);
  }, [query, communityList]);
  // Community rows are metadata only — fetch the full body before scheduling.
  const pickCommunity = async (c) => {
    if (fetchingId) return;
    setFetchingId(c._communityRecipeId);
    try {
      const { data, error } = await supabase
        .from("user_recipes")
        .select("recipe, slug")
        .eq("id", c._communityRecipeId)
        .maybeSingle();
      if (error || !data?.recipe) throw error || new Error("recipe unavailable");
      onPick({
        ...data.recipe,
        slug: data.slug || c.slug,
        _source: "community",
        _communityRecipeId: c._communityRecipeId,
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[plan] community recipe fetch failed:", e?.message || e);
    } finally {
      setFetchingId(null);
    }
  };
  const nothingToBook = filtered.length === 0 && filteredLeftovers.length === 0 && filteredCommunity.length === 0;

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, background: "#000000dd", zIndex: 290,
      display: "flex", alignItems: "flex-end",
      maxWidth: 480, margin: "0 auto",
    }}>
      <div style={{
        width: "100%", background: COLOR.ground,
        borderRadius: "20px 20px 0 0",
        padding: "20px 22px 30px",
        maxHeight: "80vh", display: "flex", flexDirection: "column",
      }}>
        <div style={{ width: 36, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 16px" }} />
        <div style={{ fontFamily: FONT.forma, fontSize: 10, fontWeight: 700, color: COLOR.gold, letterSpacing: "0.16em" }}>
          PICK A RECIPE
        </div>
        <input
          autoFocus
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search recipes…"
          style={{
            marginTop: 10, padding: "12px 14px",
            background: COLOR.soil, border: `1px solid ${COLOR.border}`,
            borderRadius: 10, color: COLOR.ink,
            fontFamily: FONT.forma, fontSize: 14, outline: "none",
          }}
        />
        <div style={{ marginTop: 14, overflowY: "auto", flex: 1 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {filteredLeftovers.length > 0 && (
              <>
                <div style={{ fontFamily: FONT.forma, fontSize: 9, fontWeight: 700, color: COLOR.amber, letterSpacing: "0.16em", marginTop: 2, marginBottom: 2 }}>
                  LEFTOVERS · USE FIRST
                </div>
                {filteredLeftovers.map(({ pantryRow, recipe: lr }) => {
                  const servingsLeft = Number(pantryRow.servingsRemaining);
                  const servingsText = Number.isFinite(servingsLeft)
                    ? `${servingsLeft % 1 ? servingsLeft.toFixed(1) : servingsLeft} SERVING${servingsLeft === 1 ? "" : "S"} LEFT`
                    : "LEFTOVER";
                  return (
                    <button
                      key={`leftover-${pantryRow.id}`}
                      onClick={() => onPick({ ...lr, slug: lr?.slug || pantryRow.sourceRecipeSlug }, { fromPantryRowId: pantryRow.id })}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "12px 14px", background: COLOR.goldDeep,
                        border: `1px solid ${COLOR.goldDim}`, borderRadius: 12,
                        cursor: "pointer", textAlign: "left",
                      }}
                    >
                      <div style={{ fontSize: 26, flexShrink: 0 }}>{lr?.emoji || pantryRow.emoji || "🍽️"}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500, fontSize: 15, color: COLOR.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lr?.title || pantryRow.name || "Leftover"}
                        </div>
                        <div style={{ fontFamily: FONT.forma, fontSize: 10, color: COLOR.amber, marginTop: 2 }}>
                          ♨ {servingsText}
                        </div>
                      </div>
                    </button>
                  );
                })}
                <div style={{ fontFamily: FONT.forma, fontSize: 9, fontWeight: 700, color: COLOR.sub, letterSpacing: "0.16em", marginTop: 10, marginBottom: 2 }}>
                  OR COOK SOMETHING
                </div>
              </>
            )}
            {filtered.map(r => {
              const bits = [
                r.cuisine ? r.cuisine.toUpperCase() : null,
                `${totalTimeMin(r) || 0} MIN`,
                r.difficulty ? difficultyLabel(r.difficulty).toUpperCase() : null,
              ].filter(Boolean);
              const tag = r._source === "custom" ? "YOURS" : r._source === "ai" ? "AI" : null;
              return (
                <button
                  key={r._userId ? `${r._userId}:${r.slug}` : r.slug}
                  onClick={() => onPick(r)}
                  style={{
                    display: "flex", alignItems: "center", gap: 12,
                    padding: "12px 14px", background: COLOR.soil,
                    border: `1px solid ${COLOR.border}`, borderRadius: 12,
                    cursor: "pointer", textAlign: "left",
                  }}
                >
                  <div style={{ fontSize: 26, flexShrink: 0 }}>{r.emoji || "🍽️"}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500, fontSize: 15, color: COLOR.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                        {r.title || "(untitled)"}
                      </div>
                      {tag && (
                        <span style={{ fontFamily: FONT.forma, fontSize: 9, fontWeight: 700, color: COLOR.gold, background: COLOR.goldDeep, border: `1px solid ${COLOR.goldDim}`, borderRadius: 4, padding: "2px 6px", letterSpacing: "0.12em", flexShrink: 0 }}>
                          {tag}
                        </span>
                      )}
                    </div>
                    <div style={{ fontFamily: FONT.forma, fontSize: 10, color: COLOR.sub, letterSpacing: "0.06em", marginTop: 2 }}>
                      {bits.join(" · ")}
                    </div>
                  </div>
                </button>
              );
            })}
            {filteredCommunity.length > 0 && (
              <>
                <div style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, color: COLOR.sub, letterSpacing: "0.16em", marginTop: 10, marginBottom: 2 }}>
                  FROM THE COMMUNITY
                </div>
                {filteredCommunity.map(c => {
                  const busy = fetchingId === c._communityRecipeId;
                  return (
                    <button
                      key={`comm-${c._communityRecipeId}`}
                      onClick={() => pickCommunity(c)}
                      disabled={!!fetchingId}
                      style={{
                        display: "flex", alignItems: "center", gap: 12,
                        padding: "12px 14px", background: COLOR.soil,
                        border: `1px solid ${COLOR.border}`, borderRadius: 12,
                        cursor: busy ? "wait" : "pointer", textAlign: "left",
                        opacity: fetchingId && !busy ? 0.5 : 1,
                      }}
                    >
                      <div style={{ fontSize: 26, flexShrink: 0 }}>{c.emoji || "🍽️"}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <div style={{ fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500, fontSize: 15, color: COLOR.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1, minWidth: 0 }}>
                            {c.title}
                          </div>
                          <span style={{ fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, color: COLOR.sky, background: COLOR.skyDeep, border: `1px solid ${COLOR.skyBorder}`, borderRadius: 4, padding: "2px 6px", letterSpacing: "0.10em", flexShrink: 0 }}>
                            COMMUNITY
                          </span>
                        </div>
                        <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, letterSpacing: "0.06em", marginTop: 2 }}>
                          {busy ? "Loading…" : [c.cuisine ? c.cuisine.toUpperCase() : null, c._authorName ? `BY ${c._authorName.toUpperCase()}` : null].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </>
            )}
            {nothingToBook && (
              <div style={{ padding: "24px 16px", textAlign: "center" }}>
                <div style={{ color: COLOR.sub, fontFamily: FONT.forma, fontSize: 13, lineHeight: 1.5 }}>
                  {query ? `Nothing matches "${query}" yet.` : "No recipes to book yet — nothing of yours, no leftovers, nothing from the community."}
                </div>
                {onCreateRecipe && (
                  <button
                    onClick={onCreateRecipe}
                    style={{
                      marginTop: 14, padding: "13px 22px", background: COLOR.leaf, color: "#fff",
                      border: "none", borderRadius: 12, fontFamily: FONT.forma, fontSize: 13, fontWeight: 700,
                      letterSpacing: "0.04em", cursor: "pointer",
                    }}
                  >
                    + Create a recipe
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            marginTop: 12, padding: "14px",
            background: COLOR.soil, border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 12,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 700,
            letterSpacing: "0.12em", cursor: "pointer",
          }}
        >
          CANCEL
        </button>
      </div>
    </div>,
    document.body,
  );
}
